import api from './base';

// Currency conversion types
export interface CurrencyRate {
    from: string;
    to: string;
    rate: number;
    timestamp: Date;
    source: 'frankfurter' | 'cache';
}

export interface ConversionRequest {
    amount: number;
    from: string;
    to: string;
    date?: string; // For historical rates
}

export interface ConversionResponse {
    originalAmount: number;
    convertedAmount: number;
    rate: number;
    from: string;
    to: string;
    timestamp: Date;
    cached: boolean;
}

export interface Currency {
    code: string;
    name: string;
    symbol: string;
    flag?: string;
}

export interface BatchConversionRequest {
    conversions: ConversionRequest[];
}

export interface HealthCheckResponse {
    status: 'healthy' | 'degraded' | 'error';
    apiStatus: string;
    cacheStatus: string;
    lastSuccessfulFetch?: Date;
    supportedCurrencies: number;
}

// Currency API service
class CurrencyApiService {
    private baseUrl = '/currency';
    private readonly REQUEST_TIMEOUT = 60000;
    private readonly MAX_RETRIES = 2;

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private isRetryableError(error: any): boolean {
        const status = error?.response?.status;

        return (
            status === 429 ||
            error?.code === 'ECONNABORTED' ||
            error?.code === 'ERR_NETWORK' ||
            !error?.response
        );
    }

    private getRetryDelayMs(error: any, attempt: number): number {
        const retryAfterHeader = error?.response?.headers?.['retry-after'];
        const retryAfterSeconds = Number(retryAfterHeader);

        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            return retryAfterSeconds * 1000;
        }

        return 1000 * Math.pow(2, attempt);
    }

    private async withRetry<T>(operation: () => Promise<T>, context: string): Promise<T> {
        let lastError: any;

        for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
            try {
                return await operation();
            } catch (error: any) {
                lastError = error;

                if (attempt === this.MAX_RETRIES || !this.isRetryableError(error)) {
                    break;
                }

                const delayMs = this.getRetryDelayMs(error, attempt);
                console.warn(`${context} failed (attempt ${attempt + 1}). Retrying in ${delayMs}ms...`, {
                    status: error?.response?.status,
                    code: error?.code,
                });
                await this.sleep(delayMs);
            }
        }

        throw lastError;
    }

    // Get current exchange rates
    async getRates(base?: string): Promise<Record<string, number>> {
        const params = base ? `?base=${base}` : '';
        const response = await this.withRetry(
            () => api.get(`${this.baseUrl}/rates${params}`, { timeout: this.REQUEST_TIMEOUT }),
            'currency.getRates'
        );
        return response.data.data.rates;
    }

    // Get specific rate between two currencies
    async getRate(from: string, to: string): Promise<CurrencyRate> {
        const response = await api.get(`${this.baseUrl}/rates/${from}/${to}`, { timeout: this.REQUEST_TIMEOUT });
        return response.data.data;
    }

    // Convert amount between currencies
    async convertAmount(request: ConversionRequest): Promise<ConversionResponse> {
        const response = await api.post(`${this.baseUrl}/convert`, request);
        return response.data.data;
    }

    // Get historical rate for specific date
    async getHistoricalRate(from: string, to: string, date: string): Promise<CurrencyRate> {
        const response = await api.get(`${this.baseUrl}/historical/${date}/${from}/${to}`);
        return response.data.data;
    }

    // Get list of supported currencies
    async getSupportedCurrencies(): Promise<Currency[]> {
        const response = await this.withRetry(
            () => api.get(`${this.baseUrl}/supported`, { timeout: this.REQUEST_TIMEOUT }),
            'currency.getSupportedCurrencies'
        );
        return response.data.data.currencies;
    }

    // Batch currency conversions
    async convertBatch(request: BatchConversionRequest): Promise<ConversionResponse[]> {
        const response = await api.post(`${this.baseUrl}/convert/batch`, request);
        return response.data.data;
    }

    // Refresh currency cache (admin)
    async refreshCache(): Promise<{ success: boolean; message: string }> {
        const response = await api.post(`${this.baseUrl}/refresh-cache`);
        return response.data.data;
    }

    // Check service health
    async getHealthStatus(): Promise<HealthCheckResponse> {
        const response = await api.get(`${this.baseUrl}/health`);
        return response.data.data;
    }
}

export const currencyApi = new CurrencyApiService();